import React, { useState, useEffect, useCallback, useRef } from 'react';
import AnimacionDemo from './AnimacionDemo';
import AnimacionDemo2 from './AnimacionDemo2';
import AnimacionDemo3 from './AnimacionDemo3';
import AnimacionDemo4 from './AnimacionDemo4';
import AnimacionDemo5 from './AnimacionDemo5';
import AnimacionDemo6 from './AnimacionDemo6';
import AnimacionDemo7 from './AnimacionDemo7';
import Boton from './components/boton';
import { motion, AnimatePresence } from "framer-motion";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';

import {
  MessageCircle,
  Box,
  Play,
  Key,
  Home,
  Building,
  Sparkles,
  Bot,
  CheckCircle,
  CheckCircle2,
  ChevronDown,
  Clock,
  Globe,
  Menu,
  X,
  TrendingUp,
  Zap,
  Eye,
  DollarSign,
  Filter,
  User,
} from 'lucide-react';

// ─── COMPONENTE DE PARTÍCULAS PARA HERO ──────────────────────────────
const HeroParticles = ({ config }) => {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const animationFrameRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    const resizeCanvas = () => {
      const width = canvas.parentElement.offsetWidth;
      const height = canvas.parentElement.offsetHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';
      ctx.scale(dpr, dpr);
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const createParticles = () => {
      const particles = [];
      const count = config.particles.number.value;
      const width = canvas.parentElement.offsetWidth;
      const height = canvas.parentElement.offsetHeight;

      for (let i = 0; i < count; i++) {
        particles.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * config.particles.move.speed,
          vy: -(Math.random() - 0.5) * config.particles.move.speed, // Velocidad vertical más lenta para efecto de elevación
          size: Math.random() * (config.particles.size.value - 1) + 1,
          opacity: Math.random() * 1 + 1,
        });
      }
      return particles;
    };

    particlesRef.current = createParticles();

    const drawPolygon = (x, y, size, sides = 6) => {
      ctx.beginPath();
      for (let i = 0; i < sides; i++) {
        const angle = (i / sides) * Math.PI * 2;
        const px = x + size * Math.cos(angle);
        const py = y + size * Math.sin(angle);
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fill();
    };

    const animate = () => {
      const width = canvas.parentElement.offsetWidth;
      const height = canvas.parentElement.offsetHeight;

      ctx.clearRect(0, 0, width, height);

      particlesRef.current.forEach((particle) => {
        particle.x += particle.vx;
        particle.y += particle.vy;

        if (particle.x < 0 || particle.x > width) particle.vx *= -1;
        if (particle.y < 0 || particle.y > height) particle.vy *= -1;

        ctx.fillStyle = `rgba(255, 255, 255, ${particle.opacity})`;
        drawPolygon(particle.x, particle.y, particle.size);

        particlesRef.current.forEach((other) => {
          const dx = particle.x - other.x;
          const dy = particle.y - other.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < config.particles.line_linked.distance) {
            ctx.strokeStyle = `rgba(255, 255, 255, ${0.15 * (1 - distance / config.particles.line_linked.distance)})`;
            ctx.lineWidth = config.particles.line_linked.width;
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(other.x, other.y);
            ctx.stroke();
          }
        });
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [config]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none"
      style={{ background: 'transparent' }}
    />
  );
};

export default function App() {
  const [activeFaq, setActiveFaq] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [navbarHidden, setNavbarHidden] = useState(false);
  // ─── DETECCIÓN DE PANTALLA MÓVIL ─────────────────────────────────
  const [isMobile, setIsMobile] = useState(false);
  const lastScrollY = useRef(0);
  const navbarRef = useRef(null);

  // Detecta tamaño de pantalla para estilos condicionales
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // ─── CONFIGURACION DE PARTICULAS ─────────────────────────────────
  const particlesConfig = {
    particles: {
      number: { value: 100, density: { enable: true, value_area: 15 } },
      color: { value: '#fbbf24' },
      shape: { type: 'polygon', polygon: { nb_sides: 6 } },
      opacity: { value: 1, random: false, anim: { enable: false, speed: 3, opacity_min: 0.3, sync: false } },
      size: { value: 1.5, random: true, anim: { enable: true, speed: 4, size_min: 1, sync: false } },
      line_linked: { enable: true, distance: 200, color: '#ffffff', opacity: 1, width: 1 },
      move: { enable: true, speed: 0.2, direction: 'top', random: true, straight: false, out_mode: 'out', bounce: true },
    },
    interactivity: {
      detect_on: 'canvas',
      events: { onhover: { enable: true, mode: 'grab' }, onclick: { enable: true, mode: 'bubble' }, resize: true },
      modes: {
        grab: { distance: 200, line_linked: { opacity: 0.5 } },
        bubble: { distance: 200, size: 6, duration: 2, opacity: 1, speed: 2 },
      },
    },
    retina_detect: true,
  };

  // ─── SCROLL HANDLER ──────────────────────────────────────────────
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > 100) {
        setScrolled(true);
        if (window.innerWidth >= 1024) {
          if (currentScrollY > lastScrollY.current) {
            setNavbarHidden(false);
          } else {
            setNavbarHidden(false);
          }
        }
      } else {
        setScrolled(false);
        setNavbarHidden(false);
      }
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Bloquea scroll cuando el menú móvil está abierto
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const toggleFaq = (index) => setActiveFaq(activeFaq === index ? null : index);
  const closeMobileMenu = () => setMobileMenuOpen(false);

  const navLinks = [
    { href: 'https://7proptech.com/3d-tours/', label: '3D TOURS' },
    { href: 'https://7proptech.com/site-selection/', label: 'SITE SELECTION' },
    { href: '#demo', label: 'FINTECH' },
    { href: '#precios', label: 'IA' },
  ];

  const faqs = [
    {
      question: '¿Cuánto tiempo tardan en escanear la propiedad?',
      answer: 'El proceso es increíblemente rápido y no invasivo. Dependiendo del tamaño del inmueble, nuestro equipo especializado tarda entre 2 a 4 horas en proyectos estándar. Mientras tanto, tú puedes seguir con tus actividades normales.',
    },
    {
      question: '¿Funciona en dispositivos móviles?',
      answer: 'Sí, al 100%. Toda nuestra infraestructura digital está optimizada para funcionar con fluidez en cualquier smartphone, tablet o computadora.',
    },
    {
      question: '¿Puedo integrarlo a portales inmobiliarios?',
      answer: 'Por supuesto. Te entregamos enlaces compatibles y códigos de inserción (embeds) que funcionan perfectamente en plataformas como Inmuebles24, Vivanuncios y Airbnb.',
    },
  ];

  // ─── ESTILOS ADAPTATIVOS PARA HERO HEADINGS ──────────────────────
  // Desktop: márgenes negativos + display flex originales (efecto visual del diseño).
  // Móvil:   display BLOCK (evita que el texto se salga de pantalla con flex+justify-end),
  //          sin márgenes negativos, fuente escalada con clamp.
  const heroH1Style = {
    fontFamily: "Space Grotesk, sans-serif",
    // FIX: display:flex + justifyContent:flex-end en móvil empujaba el texto
    //      fuera de pantalla. Display:block lo mantiene visible y alineado a la izquierda.
    display: isMobile ? "block" : "flex",
    fontSize: 'clamp(26px, 8vw, 80px)',
    fontWeight: '900',
    lineHeight: isMobile ? '1.0' : '0.80',
    letterSpacing: isMobile ? '-1px' : '-2px',
    textAlign: "left",
    textTransform: "uppercase",
    marginLeft: isMobile ? "0px" : "0px",
    marginTop: isMobile ? "0px" : "0px",
  };

  const heroH2Style = {
    fontFamily: "Space Grotesk, sans-serif",
    // FIX: mismo problema — display:block en móvil
    display: isMobile ? "block" : "flex",
    fontSize: 'clamp(26px, 8vw, 80px)',
    fontWeight: '900',
    lineHeight: isMobile ? '1.05' : '0.92',
    color: "#EAB308",
    textAlign: "left",
    letterSpacing: isMobile ? '0px' : '2px',
    textTransform: "uppercase",
    marginLeft: isMobile ? "0px" : "0px",
    marginTop: "5px",
    marginBottom: isMobile ? "8px" : "-30px",
  };

  return (
    <div className="bg-slate-950 relative selection:bg-yellow-500 selection:text-slate-900 overflow-x-hidden m-0 p-0">

      {/* ─── BOTÓN FLOTANTE AGENTE CHATBOT ─────────────────────────────────── */}
      <button
        className="fixed hidden fixed bottom-6 right-6 z-60"
        onClick={() => window.botpressWebChat.sendEvent({ type: "show" })}
      >
      </button>


      {/* ─── NAVEGACIÓN ──────────────────────────────────────────────── */}
      <nav
        ref={navbarRef}
        className={`fixed top-0 w-full text-white py-3 sm:py-4 px-4 sm:px-6 lg:px-8 flex justify-between items-center z-40 border-slate-100 transition-all duration-300 ${scrolled ? 'bg-slate-950/95 backdrop-blur-md border-white/5 py-3' : 'bg-transparent'
          } ${navbarHidden ? '-translate-y-full' : 'translate-y-0'}`}
      >
        {/* Logo */}
        <div className="flex items-center space-x-2 group cursor-pointer flex-shrink-0 min-w-0">
          <Box
            className="text-yellow-500 flex-shrink-0 transition-transform duration-500 group-hover:rotate-180"
            style={{ width: '25px', height: '25px' }}
          />
          <div
            className="text-lg font-bold tracking-tight overflow-hidden"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          >
            <span style={{ fontSize: '20px' }}>7</span>
            <span style={{
              fontFamily: 'Space Grotesk, sans-serif',
              fontWeight: 300,
              fontSize: '20px'
            }}>
              PropTech
            </span>
            {/*
              FIX NAVBAR MÓVIL:
              "xs:" no es un breakpoint estándar de Tailwind → el pipe siempre estaba oculto.
              "sm:inline" (640px) aún aparece en teléfonos grandes.
              Usamos "md:inline" (768px) para que pipe y subtítulo se muestren
              sólo en tablets/desktop y se oculten en cualquier teléfono Android.
            */}
            <span
              className="ml-1 font-semibold tracking-wide hidden md:inline"
              style={{ color: '#ffffff', fontSize: '20px', letterSpacing: '0.8px' }}
            >
              {' '}|
            </span>
            <span
              className="ml-1 hidden md:inline italic"
              style={{ fontSize: '15px', fontWeight: '200', opacity: '0.5', letterSpacing: '1px', fontFamily: 'Space Grotesk, sans-serif' }}
            >
              DIGITAL TWIN INFRASTRUCTURE
            </span>
          </div>
        </div>

        {/* Desktop nav links */}
        <div
          className="hidden lg:flex space-x-6 xl:space-x-8 text-xs tracking-widest items-center uppercase"
          style={{ fontFamily: 'Space Grotesk, sans-serif' }}
        >
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="hover:text-yellow-500 transition-all duration-300 transform hover:scale-110"
            >
              {link.label}
            </a>
          ))}
          <a
            href="https://7proptech.com/"
            className="bg-yellow-500 text-slate-900 px-4 xl:px-5 py-2 rounded-sm hover:bg-white transition-all transform hover:scale-110 whitespace-nowrap"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          >
            HOME
          </a>
        </div>

        {/* Hamburger button */}
        <button
          onClick={() => setMobileMenuOpen(true)}
          className="lg:hidden p-2 rounded-md text-slate-300 hover:text-yellow-500 hover:bg-slate-800 transition-all active:scale-90 flex-shrink-0"
          aria-label="Abrir menú"
        >
          <Menu className="w-6 h-6" />
        </button>
      </nav>

      {/* ─── MENÚ MÓVIL (overlay) ─────────────────────────────────────── */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 lg:hidden"
              onClick={closeMobileMenu}
            />
            <motion.div
              key="panel"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed top-0 right-0 h-full w-4/5 max-w-sm bg-slate-900 border-l border-slate-700 z-50 p-6 shadow-2xl lg:hidden flex flex-col"
            >
              {/* Cabecera del menú */}
              <div className="flex justify-between items-center w-full mb-10">
                <div className="flex items-center">
                  <Box className="text-yellow-500 w-5 h-5 flex-shrink-0" />
                  <span className="text-white font-bold text-base ml-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>7</span>
                  <span className="font-thin text-slate-300 text-sm ml-0" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>PropTech</span>
                </div>
                <button
                  onClick={closeMobileMenu}
                  className="p-2 rounded-full text-slate-400 hover:text-yellow-500 hover:bg-slate-800 transition-all"
                  aria-label="Cerrar menú"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Links del menú */}
              <nav className="flex flex-col space-y-2 flex-1">
                {navLinks.map((link, i) => (
                  <motion.a
                    key={link.href}
                    href={link.href}
                    onClick={closeMobileMenu}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.07 + 0.1 }}
                    className="text-slate-300 hover:text-yellow-500 font-bold tracking-widest uppercase text-sm py-4 px-4 rounded-lg hover:bg-slate-800 transition-all border-b border-slate-800 last:border-0"
                  >
                    {link.label}
                  </motion.a>
                ))}
                <motion.a
                  href="#demo"
                  onClick={closeMobileMenu}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="mt-6 bg-yellow-500 text-slate-900 px-6 py-4 rounded-sm font-black uppercase tracking-widest text-center hover:bg-white transition-all"
                  style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  HOME
                </motion.a>
              </nav>

              {/* Footer del menú */}
              <p className="text-slate-600 text-xs text-center mt-6 font-mono">
                © 2026 7PropTech | Digital Twin Infrastructure
              </p>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ─── HERO ────────────────────────────────────────────────────── */}
      <header className="relative bg-black text-white py-20 sm:py-28 lg:py-32 px-4 sm:px-6 lg:px-20 min-h-[100vh] flex items-center overflow-hidden">

        {/* Imagen de fondo */}
        <div className="absolute inset-0 z-0">
          <img
            src="Dollhouse-View(1).jpg"
            alt="Dollhouse View de una propiedad"
            className="w-full h-full object-cover opacity-60 scale-105"
            onError={(e) => {
              e.target.src = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1920&q=80';
            }}
          />
        </div>

        {/* Degradado */}
        <div className="absolute inset-0 z-10 bg-gradient-to-r from-slate-950 via-slate-950/80 to-transparent pointer-events-none" />

        {/* Patrón de puntos */}
        <div className="absolute inset-0 z-20 opacity-40 sm:opacity-50 bg-[radial-gradient(#fbbf24_1px,transparent_1px)] [background-size:24px_24px] sm:[background-size:30px_30px] pointer-events-none [mask-image:linear-gradient(to_bottom,white,transparent)]" />

        {/* Canvas para partículas */}
        <div className="absolute inset-0 z-10">
          <HeroParticles config={particlesConfig} />
        </div>

        {/* Contenido */}
        <div className="relative z-30 max-w-4xl w-full  lg:mt-50" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>

          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-yellow-500/10 border border-yellow-500/30 backdrop-blur-md px-3 sm:px-4 py-2 rounded-full mb-6 sm:mb-8">
            <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse flex-shrink-0" />
            <span className="text-[10px] sm:text-xs font-bold tracking-widest text-yellow-500 uppercase">
              SITE SELECTION | FINTECH | IA
            </span>
          </div>

          {/* ─── HEADINGS DEL HERO ─────────────────────────────────── */}
          <div className="text-left mb-8 lg:mb-12">
            <h1 style={heroH1Style}>
              NO VENDAS METROS
            </h1>
            <h1 style={heroH2Style}>
              VENDE VALOR.
            </h1>
          </div>

          {/* ─── DESCRIPCIÓN HERO ──────────────────────────────────── */}
          <p
            className="text-slate-500 mb-8 sm:mb-10 max-w-2xl leading-relaxed mt-6 sm:mt-8"
            style={{
              fontFamily: 'Space Grotesk, sans-serif',
              fontSize: isMobile ? '16px' : '22px',
              fontWeight: '300',
            }}
          >
            <strong className="text-slate-100">Fusionamos </strong>
            <span className="text-yellow-500 text-bold ">Consultoría </span>
            <span style={{ color: '#ffffff' }}>
              de Site Selection, Gemelos Digitales e infraestructura Fintech. Maximizamos el rendimiento de tus activos mediante procesos automatizados de renta y tokenización en tiempo real.
            </span>
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <a
              href="#token-card"
              className="relative flex justify-center items-center gap-2 text-slate-950 text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-bold tracking-wide shadow-lg shadow-yellow-500/20 overflow-hidden transform transition-transform duration-300 ease-out hover:scale-105 active:scale-95 bg-yellow-500 w-full sm:w-auto"
            >
              <Play className="w-5 h-5 fill-current flex-shrink-0" />
              <span className="whitespace-nowrap" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Tokenizar activo</span>
            </a>
            <a
              href="#contacto"
              className="inline-flex items-center justify-center border border-white/30 text-white transition-all transform hover:scale-105 active:scale-95 px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-bold w-full sm:w-auto text-base sm:text-lg"
            >
              Contacto
            </a>
          </div>
        </div>
      </header>

      {/* ─── SECCIÓN DE DOLORES ──────────────────────────────────────── */}
      <section id="dolores" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-20 bg-slate-800 border-y border-slate-200 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-slate-500/50 to-transparent rounded-full blur-3xl -z-10 translate-x-1/2 -translate-y-1/2" />

        <div className="max-w-8xl mx-auto">
          <div className="text-center mb-12 sm:mb-20 space-y-4">
            <h2
              className="text-3xl sm:text-4xl lg:text-6xl font-black text-slate-50 uppercase tracking-widest"
              style={{ fontFamily: 'Space Grotesk, sans-serif', WebkitTextStroke: '0px black', textShadow: '0 10px 8px rgba(0,0,0,0.3)' }}
            >
              Maximiza el Rendimiento
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto rounded-full" />
            <div className="hidden"><AnimacionDemo /><AnimacionDemo2 /></div>
          </div>

          {/* Cards — stack on mobile, 3 cols on md+ */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 lg:gap-12">

            {/* Tarjeta 1 */}
            <div className="relative group bg-slate-950 p-8 sm:p-10 border-t-4 border-yellow-500 rounded-2xl hover:-translate-y-4 hover:scale-105 hover:shadow-[0_20px_50px_-15px_rgba(30,58,138,7)] transition-all duration-500 overflow-hidden md:-translate-y-4">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#484747] rounded-bl-full blur-3xl -z-10 group-hover:scale-150 transition-transform duration-700" />
              <User className="text-yellow-500 group-hover:text-slate-100 group-hover:scale-110 transition-transform duration-500 mb-6 w-12 h-10" />
              <h3 className="text-xl sm:text-[25px] font-black mb-3 text-white uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Rentas
              </h3>
              <p className="text-slate-500 font-medium text-base sm:text-lg leading-relaxed">
                Elimina las visitas físicas innecesarias de curiosos que no rentan. Filtra prospectos con tecnología.
              </p>
            </div>

            {/* Tarjeta 2 */}
            <div className="group bg-slate-950 p-8 sm:p-10 border-t-4 border-yellow-500 rounded-2xl hover:-translate-y-4 hover:scale-105 hover:shadow-[0_20px_50px_-15px_rgba(30,58,138,7)] transition-all duration-500 relative overflow-hidden md:-translate-y-4">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#484747] rounded-bl-full blur-3xl -z-10 group-hover:scale-150 transition-transform duration-700" />
              <Home className="text-yellow-500 group-hover:text-[#0e45dc] group-hover:scale-110 transition-transform duration-500 mb-6 w-12 h-10" />
              <h3 className="text-xl sm:text-2xl font-black mb-3 text-white uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                AIRBNB
              </h3>
              <p className="text-slate-500 font-medium text-base sm:text-lg leading-relaxed">
                Muestra la calidad real de tu espacio, genera confianza absoluta y aumenta tu tarifa por noche.
              </p>
            </div>

            {/* Tarjeta 3 */}
            <div className="group bg-slate-950 p-8 sm:p-10 border-t-4 border-yellow-500 rounded-2xl hover:-translate-y-4 hover:scale-105 hover:shadow-[0_20px_50px_-15px_rgba(30,58,138,7)] transition-all duration-500 relative overflow-hidden md:-translate-y-4 sm:col-span-2 md:col-span-1">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#484747] rounded-bl-full blur-3xl -z-10 group-hover:scale-150 transition-transform duration-700" />
              <Building className="text-yellow-500 group-hover:text-slate-100 group-hover:scale-110 transition-transform duration-500 mb-6 w-12 h-10" />
              <h3 className="text-xl sm:text-2xl font-black text-white uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Desarrollos
              </h3>
              <p className="text-slate-500 font-medium text-base sm:text-lg leading-relaxed">
                Mantén tu Showroom abierto al mundo las 24 horas del día. Cierra ventas a distancia sin fricción.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── SOLUCIÓN TECNOLÓGICA ─────────────────────────────────────── */}
      <section className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-20 bg-gradient-to-b from-slate-950 via-slate-700/40 to-slate-950 text-white overflow-hidden relative">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-slate-950 rounded-full blur-3xl" />
          <div className="absolute top-10 left-15 w-96 h-96 bg-slate-950 rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto relative z-10">

          {/* Bloque 1 */}
          <div className="flex flex-col md:flex-row items-center gap-8 sm:gap-16 mb-12 sm:mb-24 lg:mb-32">
            <div className="w-full md:w-1/2">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-slate-900 border border-slate-800 hover:-translate-y-2 transition-all rounded-2xl flex items-center justify-center mb-5 sm:mb-6 text-yellow-500 shadow-inner">
                <Box className="w-6 h-6 sm:w-8 sm:h-8" />
              </div>
              <section className="hero"><AnimacionDemo3 /></section>
              <div className="text-slate-400 text-base sm:text-lg leading-relaxed mt-4">
                <section className="hero"><AnimacionDemo5 /></section>
              </div>
            </div>
            <div className="w-full md:w-1/2 bg-slate-900 rounded-xl aspect-video relative overflow-hidden shadow-2xl">
              <section className="hero"><AnimacionDemo7 /></section>
            </div>
          </div>

          {/* Bloque 2 */}
          <div className="flex flex-col md:flex-row-reverse items-center gap-8 sm:gap-16">
            <div className="w-full md:w-1/2">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-slate-900 border border-slate-800 hover:-translate-y-2 transition-all rounded-2xl flex items-center justify-center mb-5 sm:mb-6 text-yellow-500 shadow-inner">
                <Sparkles className="w-6 h-6 sm:w-8 sm:h-8" />
              </div>
              <section className="hero"><AnimacionDemo4 /></section>
              <div className="text-slate-400 text-base sm:text-lg leading-relaxed mt-4">
                <section className="hero"><AnimacionDemo6 /></section>
              </div>
            </div>
            <div className="w-full md:w-1/2 flex gap-3 sm:gap-4 h-44 sm:h-64">
              <div className="w-1/2 bg-slate-800 rounded-lg overflow-hidden border border-slate-700">
                <img src="ANTES.jpg" className="w-full h-full object-cover grayscale opacity-50" alt="Antes" />
              </div>
              <div className="w-1/2 bg-yellow-500/10 rounded-lg overflow-hidden border border-yellow-500/50">
                <img src="DESPUES.jpg" className="w-full h-full object-cover" alt="Después" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── DEMO INTERACTIVA ─────────────────────────────────────────── */}
      <section id="twin" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-20 bg-slate-950 relative overflow-hidden border-t border-slate-800 isolation-isolate">
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none z-0"
          style={{
            backgroundImage: 'linear-gradient(#fbbf24 1px, transparent 1px), linear-gradient(90deg, #fbbf24 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />

        <div className="text-center mb-10 sm:mb-16 lg:mb-20 relative z-10">
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl text-white font-extrabold mb-4 sm:mb-6 leading-tight uppercase"
            style={{ fontFamily: 'Space Grotesk, sans-serif', WebkitTextStroke: '0.8px black', textShadow: '0 10px 8px rgba(0,0,0,0.3)' }}
          >
            RECORRIDO <span className="text-yellow-500">VIRTUAL</span>
          </h2>
          <p className="text-slate-300 text-sm sm:text-base lg:text-lg max-w-2xl mx-auto leading-relaxed" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Potencia tu portafolio con tecnología 3D.
          </p>
        </div>

        <div
          className="w-full rounded-xl shadow-2xl overflow-hidden border-4 sm:border-8 border-gray-700 bg-slate-200 relative z-20"
          style={{ aspectRatio: '16/9', minHeight: isMobile ? '220px' : '260px' }}
        >
          <iframe
            title="Matterport Demo"
            src="https://my.matterport.com/show/?m=vKcUYLs62N2"
            width="100%"
            height="100%"
            frameBorder="0"
            allowFullScreen
            allow="autoplay; fullscreen; web-share; xr-spatial-tracking"
            style={{ display: 'block', position: 'relative', zIndex: 30, minHeight: isMobile ? '220px' : '260px' }}
          />
        </div>
      </section>

      {/* ─── SITE SELECTION ───────────────────────────────────────────── */}
      <section className="w-full px-4 sm:px-6 py-10 sm:py-12 lg:px-12 bg-slate-900">
        <div className="max-w-6xl mx-auto">
          <h2
            className="text-xl sm:text-2xl lg:text-4xl px-0 sm:px-10 py-6 sm:py-10 font-bold text-center uppercase tracking-widest text-slate-50"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          >
            SITE SELECTION & ESTRATEGIC ADVISOR
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 mt-2 sm:mt-8" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {[
              {
                title: 'Due Diligence',
                body: 'Inteligencia geoespacial y consultoría técnica de alto nivel para la expansión en México.',
                body2: 'Validamos con datos la viabilidad logística, el Due Diligence Técnico-legal y la optimización financiera de sus activos inmobiliarios más importantes.',
              },
              {
                title: 'Data Intelligence',
                body: 'Combinamos inteligencia de datos avanzada, análisis de riesgo y tecnología 3D para guiar a fondos de inversión, corporativos e inversionistas hacia la ubicación con el mayor rendimiento y el menor riesgo operativo en México.',
              },
              {
                title: 'Portafolio Off-Market',
                body: 'Contamos con un inventario privado y exclusivo de macrolotes para desarrollos industriales, logísticos, comerciales y turísticos en México.',
              },
            ].map((card) => (
              <div
                key={card.title}
                className="flex flex-col items-start bg-slate-700 rounded-3xl px-5 sm:px-6 py-5 sm:py-6 w-full shadow-lg"
              >
                <div className="flex gap-2 items-center text-sm sm:text-base lg:text-lg font-semibold mb-3 sm:mb-4 text-white">
                  <CheckCircle className="text-yellow-500 flex-shrink-0 w-5 h-5" /> {card.title}
                </div>
                <p className="text-slate-300 text-sm leading-relaxed">{card.body}</p>
                {card.body2 && <p className="text-slate-300 text-sm mt-3 leading-relaxed">{card.body2}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FINTECH: TOKENIZACIÓN DE NOCHES ─────────────────────────── */}
      <section id="fintech" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-20 bg-slate-950 relative overflow-hidden border-t border-slate-800">
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{ backgroundImage: 'linear-gradient(#06b6d4 2px, transparent 1px), linear-gradient(90deg, #06b6d4 2px, transparent 1px)', backgroundSize: '60px 60px' }}
        />
        <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-slate-600/20 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Badge */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 px-4 py-2 rounded-full">
              <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
              <span className="text-[10px] sm:text-xs font-bold tracking-widest text-yellow-500 uppercase" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                FINTECH LAYER · REAL ESTATE TOKENIZATION
              </span>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 sm:gap-16 items-center">
            {/* Texto */}
            <div>
              <h2
                className="text-4xl text-white sm:text-5xl lg:text-6xl font-black uppercase leading-none tracking-tight mb-6"
                style={{ fontFamily: 'Space Grotesk, sans-serif', letterSpacing: isMobile ? '-1px' : '-2px' }}
              >
                INVIERTE EN<br />
                <span className="text-yellow-500">NOCHES,</span><br />
                NO EN MUROS.
              </h2>
              <p className="text-slate-400 text-base sm:text-lg leading-relaxed mb-8 sm:mb-10 max-w-lg">
                Digitalizamos las reglas operativas de propiedades Premium. Nuestra infraestructura conecta tus noches de alojamiento a un entorno digital blindado que elimina el fraude tradicional, optimiza la liquidez de las instancias y abre canales de comercialización anticipada para el mercado global.
              </p>
              <div className="flex gap-4">
                <a
                  href="#contacto"
                  className="bg-yellow-500 text-slate-950 px-6 py-3 rounded-lg font-black uppercase tracking-widest hover:bg-white transition-all transform hover:scale-105 text-xs sm:text-sm"
                  style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                >
                  DIRECTO A LA ACCIÓN
                </a>
              </div>
            </div>

            {/* Token Card */}
            <div id="token-card" className="relative flex justify-center">
              <div className="relative w-full max-w-sm mx-auto">
                <div className="absolute inset-0 bg-yellow-500/20 rounded-3xl blur-xl translate-y-4 scale-95" />
                <div className="relative bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 rounded-3xl p-6 sm:p-8 shadow-2xl">
                  <div className="flex justify-between items-start mb-6 sm:mb-8">
                    <div>
                      <div className="text-xs text-slate-500 uppercase tracking-widest mb-1">ASSET TOKEN:</div>
                      <div className="text-xl sm:text-2xl font-black text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>7PT-001</div>
                    </div>
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-yellow-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <Box className="w-5 h-5 sm:w-6 sm:h-6 text-slate-950" />
                    </div>
                  </div>

                  <div className="bg-slate-950/60 border border-slate-700 rounded-2xl p-4 mb-5 sm:mb-6">
                    <div className="text-xs text-slate-500 mb-1 uppercase tracking-widest">Propiedad</div>
                    <div className="font-bold text-white text-sm" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Suite Frente al Mar · Tulum, QR</div>
                    <div className="flex justify-between items-center mt-3 gap-2">
                      <div>
                        <div className="text-xs text-slate-500">Precio/Noche</div>
                        <div className="text-yellow-500 font-black text-base sm:text-lg" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>$4,500 MXN</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Ocupación</div>
                        <div className="text-white font-black text-base sm:text-lg" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>87%</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Tokens</div>
                        <div className="text-white font-black text-base sm:text-lg" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>1,000</div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-5 sm:mb-6">
                    <div className="flex justify-between text-xs text-slate-500 mb-2">
                      <span>Tokens vendidos</span>
                      <span className="text-yellow-500 font-bold">73%</span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-2">
                      <div className="bg-yellow-500 h-2 rounded-full" style={{ width: '73%' }} />
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-4 border-t border-slate-700">
                    <div className="text-xs text-slate-500">Último reparto</div>
                    <div className="text-green-400 font-bold text-sm">+ $127 MXN / token</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── IA: SERVICIOS INTELIGENTES ──────────────────────────────── */}
      <section id="ia" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-20 bg-slate-950 relative overflow-hidden border-t border-slate-800">
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{ backgroundImage: 'linear-gradient(#fbbf24 1px, transparent 1px), linear-gradient(90deg, #fbbf24 1px, transparent 1px)', backgroundSize: '60px 60px' }}
        />
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-yellow-500/30 to-transparent" />
          <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-yellow-500/30 to-transparent" />
          <div className="absolute top-1/3 right-0 w-[300px] sm:w-[600px] h-[300px] sm:h-[600px] bg-yellow-500/30 rounded-full blur-3xl" />
          <div className="absolute bottom-1/3 left-0 w-[200px] sm:w-[400px] h-[200px] sm:h-[400px] bg-slate-700/20 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Encabezado */}
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6 sm:gap-8 mb-12 sm:mb-20">
            <div>
              <div className="inline-flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 px-4 py-2 rounded-full mb-4 sm:mb-6">
                <Bot className="w-3 h-3 text-yellow-400" />
                <span className="text-[10px] font-bold tracking-widest text-yellow-500 uppercase" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  INTELIGENCIA ARTIFICIAL
                </span>
              </div>
              <h2
                className="text-4xl text-white sm:text-5xl lg:text-6xl font-black uppercase leading-none"
                style={{ fontFamily: 'Space Grotesk, sans-serif', letterSpacing: isMobile ? '-1px' : '-2px' }}
              >
                IA QUE<br /><span className="text-yellow-500">TRABAJA</span><br />POR TI.
              </h2>
            </div>
            <p className="text-slate-400 text-base sm:text-lg max-w-md leading-relaxed lg:text-right">
              Vision Artificial: Seguridad patrimonial predictiva. Vende Blindaje predictivo y control operativo de Activos.
            </p>
          </div>

          {/* Grid de servicios */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 mb-10 sm:mb-16">
            {[
              {
                icon: <Bot className="w-6 h-6 sm:w-7 sm:h-7" />,
                tag: 'CONVERSIÓN',
                title: 'Agente IA 24/7',
                desc: 'IA Concierge entrenado con tu portafolio completo. Responde preguntas, califica prospectos y agenda visitas automáticamente — sin intervención humana.',
                metric: '↑ 3× leads calificados',
              },
              {
                icon: <Eye className="w-6 h-6 sm:w-7 sm:h-7" />,
                tag: 'ANÁLISIS',
                title: 'IA y Seguridad Patrimonial',
                desc: 'Implementamos IA avanzada para la protección perimetral proactiva de su propiedad, detectar puntos de riesgo sobre su infraestructura de cámaras con Gemelos Virtuales y optimización del costo operativo de seguridad.',
                metric: '↑ 40% valor percibido',
              },
              {
                icon: <TrendingUp className="w-6 h-6 sm:w-7 sm:h-7" />,
                tag: 'MANTENIMIENTO',
                title: 'Mantenimiento Predictivo y Facility Management',
                desc: 'Integración de sensores IoT para monitoreo en tiempo real del estado físico de la infraestructura, mantenimiento predictivo basado en Gemelos Virtuales y optimización de costos operativos.',
                metric: '↑ 22% RevPAR promedio',
              },
              {
                icon: <Filter className="w-6 h-6 sm:w-7 sm:h-7" />,
                tag: 'AUDITORÍA',
                title: 'Auditoría Técnica de Activos',
                desc: 'Sincronizamos el estado físico real de la obra con el modelo digital, control de desviaciones estructurales y certificaciones inmutables de condiciones físicas (As-Build) para agilizar pólizas de seguros.',
                metric: '↓ 60% riesgo de error',
              },
              {
                icon: <Globe className="w-6 h-6 sm:w-7 sm:h-7" />,
                tag: 'GEOINTELIGENCIA',
                title: 'Infraestructura Interconectada',
                desc: 'La inteligencia artificial de 7PropTech no opera de manera aislada. La visión artificial de nuestros modelos digitales valida físicamente la infraestructura sustentable (Paneles Solares, Reservas Ecológicas, etc.).',
                metric: '→ Ventaja competitiva',
              },
              {
                icon: <Zap className="w-6 h-6 sm:w-7 sm:h-7" />,
                tag: 'AUTOMATIZACIÓN',
                title: 'Logistica de Riesgo Opertivo y Simulación de Evacuación',
                desc: 'Mediante los Gemelos Digitales se realiza un análisis con IA del entorno digital para optimizar rutas de evacuación ante emergencias y cumplir con Protección Civil.',
                metric: '↓ 70% tiempo operativo',
              },
            ].map((service) => (
              <div
                key={service.title}
                className="group relative bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 hover:border-yellow-500/50 hover:-translate-y-2 transition-all duration-500 overflow-hidden"
              >
                <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-yellow-500/0 group-hover:via-yellow-500/60 to-transparent transition-all duration-500" />

                <div className="inline-flex items-center gap-1 bg-yellow-500/10 border border-yellow-500/20 px-2 py-1 rounded-full mb-4 sm:mb-6">
                  <span className="text-[9px] font-black tracking-widest text-yellow-500/80" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {service.tag}
                  </span>
                </div>

                <div className="text-yellow-500 mb-4 sm:mb-5 group-hover:scale-110 transition-transform duration-300">
                  {service.icon}
                </div>

                <h3 className="text-white font-black text-base sm:text-xl uppercase tracking-wide mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {service.title}
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed mb-5 sm:mb-6">
                  {service.desc}
                </p>

                <div className="flex items-center gap-2 pt-4 border-t border-slate-800">
                  <CheckCircle2 className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                  <span className="text-yellow-500/80 text-xs font-bold tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {service.metric}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Banner */}
          <div className="relative bg-slate-900 border border-yellow-500/30 rounded-3xl p-8 sm:p-10 lg:p-14 overflow-hidden text-center">
            <div className="relative z-10">
              <div className="flex justify-center mb-4">
                <Bot className="w-8 h-8 sm:w-10 sm:h-10 text-yellow-400" />
              </div>
              <h3
                className="text-xl sm:text-2xl lg:text-4xl font-black uppercase text-white mb-4"
                style={{ fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '-1px' }}
              >
                ¿LISTO PARA OPERAR<br /><span className="text-yellow-500">CON VENTAJA?</span>
              </h3>
              <p className="text-slate-400 text-base sm:text-lg mb-6 sm:mb-8 max-w-xl mx-auto" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Te mostraremos cómo la IA puede transformar tu portafolio actual.
              </p>
              <a
                href="#"
                className="inline-flex items-center gap-3 bg-yellow-500 text-slate-950 px-6 sm:px-8 py-3 rounded-lg font-black uppercase tracking-widest hover:bg-white transition-all transform hover:scale-105 text-sm shadow-lg shadow-yellow-500/20"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                <Bot className="w-5 h-5 sm:w-6 sm:h-6" />

              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FAQ ──────────────────────────────────────────────────────── */}
      <section className="relative bg-slate-800">
        <div className="relative z-[1] py-14 sm:py-24 px-4 sm:px-6 lg:px-20">
          <div className="max-w-3xl mx-auto">
            <h2
              className="text-xl sm:text-2xl lg:text-3xl font-bold text-center mb-8 sm:mb-16 uppercase tracking-widest text-slate-50"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              Preguntas Frecuentes
            </h2>
            <div className="space-y-3 sm:space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="border border-slate-200 rounded-lg overflow-hidden">
                  <button
                    className="w-full px-4 sm:px-6 py-4 sm:py-6 text-left flex justify-between items-center bg-slate-900 hover:bg-slate-800 active:bg-slate-700 transition-all gap-4"
                    onClick={() => toggleFaq(index)}
                  >
                    <span className="font-bold text-slate-300 text-sm sm:text-base">{faq.question}</span>
                    <ChevronDown
                      className={`w-5 h-5 text-slate-500 transition-transform flex-shrink-0 ${activeFaq === index ? 'rotate-180 text-yellow-500' : ''}`}
                    />
                  </button>
                  <div
                    className={`transition-all duration-300 ease-in-out px-4 sm:px-6 bg-slate-900 ${activeFaq === index
                      ? 'max-h-64 py-4 sm:py-6 border-t border-slate-700 opacity-100'
                      : 'max-h-0 opacity-0 overflow-hidden py-0'
                      }`}
                  >
                    <p className="text-slate-300 text-sm sm:text-base leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── CONTACTO / FOOTER ───────────────────────────────────────── */}
      <footer
        id="contacto"
        className="bg-slate-950 text-white py-14 sm:py-24 px-4 sm:px-6 lg:px-20 border-t-4 sm:border-t-8 border-yellow-500"
      >
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 sm:gap-20">

          {/* Columna izquierda */}
          <div>
            <h2
              className="text-2xl sm:text-3xl lg:text-5xl font-black mb-6 sm:mb-8 leading-tight uppercase"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              Transforma tu portafolio{' '}
              <span className="text-yellow-500">hoy mismo.</span>
            </h2>
            <div className="space-y-4 sm:space-y-6 text-sm sm:text-base">
              {[
                { Icon: Clock, text: 'Tiempo de respuesta ágil.' },
                { Icon: Globe, text: 'Cobertura Nacional de México.' },
                { Icon: Globe, text: 'Due Diligence Técnico y Legal.' },
                { Icon: Bot, text: 'Inteligencia de Datos Aplicada.' },
                { Icon: Bot, text: 'Integración con IA y Tecnología 3D.' },
                { Icon: Zap, text: 'Nearshoring.' },
              ].map(({ Icon, text }) => (
                <div key={text} className="flex items-center gap-3 sm:gap-4">
                  <Icon className="text-yellow-500 flex-shrink-0 w-5 h-5" />
                  <span className="text-slate-300">{text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Columna derecha — Formulario */}
          <div className="bg-slate-900 p-5 sm:p-10 rounded-2xl border border-slate-800 shadow-2xl">
            <form
              action="https://formspree.io/f/mbdwpneo"
              method="POST"
              className="space-y-4 sm:space-y-6"
            >
              <input
                type="text"
                name="nombre"
                placeholder="Nombre completo"
                className="w-full bg-slate-950 border border-slate-800 p-3 sm:p-4 rounded-lg focus:outline-none focus:border-yellow-500 text-white text-sm sm:text-base transition-colors"
                required
              />
              <input
                type="tel"
                name="telefono"
                placeholder="WhatsApp"
                className="w-full bg-slate-950 border border-slate-800 p-3 sm:p-4 rounded-lg focus:outline-none focus:border-yellow-500 text-white text-sm sm:text-base transition-colors"
                required
              />
              <div className="relative">
                <select
                  name="tipo"
                  defaultValue=""
                  className="w-full bg-slate-950 border border-slate-800 p-3 sm:p-4 rounded-lg focus:outline-none focus:border-yellow-500 appearance-none text-slate-400 text-sm sm:text-base transition-colors"
                  required
                >
                  <option value="" disabled>Selecciona tipo de proyecto...</option>
                  <option value="residencial">Renta Residencial</option>
                  <option value="airbnb">Airbnb / Renta Vacacional</option>
                  <option value="corporativo">Desarrollo Inmobiliario</option>
                  <option value="corporativo2">Corporativo</option>
                  <option value="comercial">Comercial</option>
                  <option value="industrial">Industrial</option>
                  <option value="turistico">Turístico</option>
                </select>
                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600 pointer-events-none" />
              </div>
              <button
                type="submit"
                className="w-full bg-yellow-500 text-slate-950 py-3 sm:py-4 font-black uppercase tracking-widest hover:bg-white active:scale-95 transition-all text-sm sm:text-base rounded-sm"
              >
                Enviar Datos
              </button>
            </form>
          </div>
        </div>

        <div
          className="mt-12 sm:mt-20 text-center text-yellow-500 font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.3em] sm:tracking-[0.5em]"
          style={{ fontFamily: 'Space Grotesk, sans-serif' }}
        >
          &copy; 2026 7PropTech | Digital Twin Infrastructure
        </div>
      </footer>

    </div>
  );
}